import React from 'react';
import { observer } from 'mobx-react-lite';
import { Icons, SvgDiv, TooltipContainer, TooltipTitle, MainItemDiv } from '../styles';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { Dropdown, DropdownDivider, DropdownItem } from '../../../component/dropdown';
import IconButton from '../../../component/button/iconButton';
import Tooltip from '../../../component/tooltip';
import SnoozeCalendar from '../snoozeCalendar';
import { ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, ICON_SIZE } from '../../../global/constants';
import { InboxActionButtonsProps } from '../../../interfaces/NotificationInterface';
import { rootStore } from '../../../mobx/rootStore';

const InboxActionButtons: React.FC<InboxActionButtonsProps> = observer(
  ({
    item,
    isTaskDetailPreview,
    mobile,
    snoozeMenuItems,
    onClickReadIcon,
    onClickDeleteIcon,
    onClickSnoozeIcon,
    handleClick,
    handleTimeChange,
    onCancelSnooze,
    onClickSnooze,
    dropdownRefCalendar,
    onDateChange
  }) => {
    const { snoozeStore } = rootStore;
    const isThisItemCalendarOpen = snoozeStore.isCalendarOpen && snoozeStore.openDropdownId === item.id;

    return (
      <Icons className='inbox-icons' isTaskDetailPreview={isTaskDetailPreview}>
        <Tooltip
          maxWidth={'max-content'}
          placement='bottom'
          title={<TooltipTitle>Mark as seen</TooltipTitle>}
          isVisibleContent={true}>
          {mobile || isTaskDetailPreview ? (
            <IconButton
              iconName='true-icon'
              size={isTaskDetailPreview ? ICON_BUTTON_SIZE.SMALL : ICON_BUTTON_SIZE.EXTRA_LARGE}
              variant={isTaskDetailPreview ? ICON_BUTTON_TYPE.TERTIARY : ICON_BUTTON_TYPE.SECONDARY}
              onClick={(e) => onClickReadIcon(item, e)}
            />
          ) : (
            <SvgDiv role='button' onClick={(e) => onClickReadIcon(item, e)}>
              <SVGIcon
                name='true-icon'
                width={ICON_SIZE.MD}
                height={ICON_SIZE.MD}
                viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                fill='var(--text-secondary)'
              />
            </SvgDiv>
          )}
        </Tooltip>

        {/* Snooze Dropdown */}
        <Dropdown
          modalTitle='Snooze'
          isRemoveMaxHeight={true}
          isMinWidth={218}
          allowOutsideScroll={isThisItemCalendarOpen}
          disablePositionChangeOnScroll={true}
          content={
            <TooltipContainer>
              <Tooltip
                maxWidth={'max-content'}
                placement='bottom'
                title={<TooltipTitle>Reschedule</TooltipTitle>}
                isVisibleContent={true}>
                {mobile || isTaskDetailPreview ? (
                  <IconButton
                    iconName='snooze-icon'
                    size={isTaskDetailPreview ? ICON_BUTTON_SIZE.SMALL : ICON_BUTTON_SIZE.EXTRA_LARGE}
                    variant={isTaskDetailPreview ? ICON_BUTTON_TYPE.TERTIARY : ICON_BUTTON_TYPE.SECONDARY}
                    onClick={() => onClickSnoozeIcon(item?.id)}
                  />
                ) : (
                  <SvgDiv role='button' onClick={() => onClickSnoozeIcon(item?.id)}>
                    <SVGIcon
                      name='snooze-icon'
                      width={ICON_SIZE.MD}
                      height={ICON_SIZE.MD}
                      viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                      fill='var(--text-secondary)'
                    />
                  </SvgDiv>
                )}
              </Tooltip>
            </TooltipContainer>
          }
          trigger='click'
          onOutsideClick={() => {
            snoozeStore.setIsCalendarOpen(false);
            snoozeStore.setOpenDropdownId(null);
          }}>
          {!isThisItemCalendarOpen &&
            snoozeMenuItems.map((dropdownItem) => (
              <DropdownItem
                key={dropdownItem.key}
                label={dropdownItem.label}
                iconName={dropdownItem.iconName}
                iconSize={16}
                iconViewBox='0 0 24 24'
                isSuffix={dropdownItem.RightSideLabel}
                onClick={() => handleClick(dropdownItem.key, item.id)}
                isSnoozeDropdown={true}
              />
            ))}
          {!isThisItemCalendarOpen && <DropdownDivider />}
          {!isThisItemCalendarOpen && (
            <MainItemDiv>
              <DropdownItem
                key='date-time'
                label='Date & Time'
                onClick={(e) => {
                  e?.stopPropagation();
                  snoozeStore.setIsCalendarOpen(true);
                  snoozeStore.setOpenDropdownId(item.id);
                }}
                isSuffix={true}
              />
            </MainItemDiv>
          )}

          {isThisItemCalendarOpen && (
            <SnoozeCalendar
              dropdownRef={dropdownRefCalendar}
              buttonHeight={snoozeStore.buttonHeight}
              position={snoozeStore.position}
              alignment={snoozeStore.alignment}
              dropdownTopOffset={snoozeStore.dropdownTopOffset}
              selectedDate={snoozeStore.selectedDate}
              selectedTime={snoozeStore.selectedTime}
              onBack={() => snoozeStore.setIsCalendarOpen(false)}
              onCancel={onCancelSnooze}
              onSnooze={onClickSnooze}
              onDateChange={onDateChange}
              onTimeChange={handleTimeChange}
            />
          )}
        </Dropdown>

        {/* Delete Icon */}
        <Tooltip
          maxWidth={'max-content'}
          placement='bottom'
          shortCutKey='E'
          title={<TooltipTitle>Archive</TooltipTitle>}
          isVisibleContent={true}>
          {mobile || isTaskDetailPreview ? (
            <IconButton
              iconName='bin-icon'
              size={isTaskDetailPreview ? ICON_BUTTON_SIZE.SMALL : ICON_BUTTON_SIZE.EXTRA_LARGE}
              variant={isTaskDetailPreview ? ICON_BUTTON_TYPE.TERTIARY : ICON_BUTTON_TYPE.SECONDARY}
              onClick={(e) => onClickDeleteIcon(item, e)}
            />
          ) : (
            <SvgDiv role='button' onClick={(e) => onClickDeleteIcon(item, e)}>
              <SVGIcon
                name='bin-icon'
                width={ICON_SIZE.MD}
                height={ICON_SIZE.MD}
                viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                fill='var(--text-secondary)'
              />
            </SvgDiv>
          )}
        </Tooltip>
      </Icons>
    );
  }
);

export default InboxActionButtons;
